import { ComponentProps, ReactNode, useRef } from 'react';
import { mergeProps, useOverlayTrigger } from 'react-aria';
import { OverlayTriggerProps, useOverlayTriggerState } from 'react-stately';

import IconButton from '~/components/scales/IconButton/IconButton';
import Popover from '~/components/scales/Popover/Popover';

type Props = Omit<ComponentProps<typeof IconButton>, 'isPressed'> & {
  children: ReactNode;
  isOpen?: OverlayTriggerProps['isOpen'];
  onOpenChange?: OverlayTriggerProps['onOpenChange'];
  placement?: ComponentProps<typeof Popover>['placement'];
};

export default function PopoverIconButton(props: Props) {
  const state = useOverlayTriggerState({
    isOpen: props.isOpen,
    onOpenChange: props.onOpenChange,
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const {
    triggerProps: { type, ...spreadableTriggerProps },
    overlayProps,
  } = useOverlayTrigger({ type: 'menu' }, state, triggerRef);

  const { children, placement, ...buttonProps } = props;

  return (
    <div className="relative z-0">
      <IconButton
        {...mergeProps(buttonProps, spreadableTriggerProps)}
        ref={triggerRef}
        isPressed={state.isOpen}
        onClick={(e) => {
          state.toggle();
          props.onClick?.(e);
        }}
      />
      {state.isOpen && (
        <Popover
          {...overlayProps}
          offset={8}
          placement={placement ?? 'bottom end'}
          state={state}
          triggerRef={triggerRef}
        >
          {children}
        </Popover>
      )}
    </div>
  );
}
